<template>
  <div class="config-manager space-y-6 animate-[fadeIn_0.6s_ease-out]">
    <!-- 页面标题区 -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F5D76E] to-[#F2C94C] flex items-center justify-center shadow-sm">
          <svg class="w-5 h-5 text-[#1B3C59]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <div>
          <h3 class="text-xl font-black text-slate-900">配置管理</h3>
        </div>
      </div>
      <button
        v-if="hasPerm(PERMISSIONS.EDIT_CONFIG)"
        @click="handleSave"
        class="px-5 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors shadow-sm"
      >
        保存变更
      </button>
    </div>

    <!-- 前台关联提示 -->
    <div class="bg-blue-50/80 backdrop-blur-sm border border-blue-100/60 rounded-2xl p-4 flex items-start gap-3 text-sm text-blue-700">
      <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div>
        <p class="font-medium mb-1">前台关联</p>
        <p class="text-blue-600">系统配置全局影响前台的积分计算、功能开关、专家匹配算法等多个核心功能</p>
      </div>
    </div>

    <!-- 配置 Tab -->
    <el-tabs v-model="activeTab" type="border-card">
      <!-- Tab 1: 全局开关 -->
      <el-tab-pane label="全局开关" name="features">
        <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-6 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
          <div class="flex items-center gap-2 mb-3">
            <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
            <h4 class="text-sm font-black text-slate-900">功能开关</h4>
          </div>
          <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

          <div class="space-y-4">
            <div class="flex items-center justify-between py-2 border-b border-slate-50">
              <div>
                <div class="text-sm font-medium text-slate-900">专家预约</div>
                <div class="text-xs text-slate-400 mt-0.5">允许用户预约专家咨询</div>
              </div>
              <el-switch v-model="config.features.enableExpertBooking" />
            </div>
            <div class="flex items-center justify-between py-2 border-b border-slate-50">
              <div>
                <div class="text-sm font-medium text-slate-900">用户投稿</div>
                <div class="text-xs text-slate-400 mt-0.5">允许用户提交内容投稿</div>
              </div>
              <el-switch v-model="config.features.enableUserContribution" />
            </div>
            <div class="flex items-center justify-between py-2 border-b border-slate-50">
              <div>
                <div class="text-sm font-medium text-slate-900">AI 诊断</div>
                <div class="text-xs text-slate-400 mt-0.5">开放 AI 深度诊断功能</div>
              </div>
              <el-switch v-model="config.features.enableAiDiagnosis" />
            </div>
            <div class="flex items-center justify-between py-2 border-b border-slate-50">
              <div>
                <div class="text-sm font-medium text-slate-900">实战演练</div>
                <div class="text-xs text-slate-400 mt-0.5">开放实战演练场景</div>
              </div>
              <el-switch v-model="config.features.enablePractice" />
            </div>
            <div class="flex items-center justify-between py-2">
              <div>
                <div class="text-sm font-medium text-slate-900">评论功能</div>
                <div class="text-xs text-slate-400 mt-0.5">允许用户发表评论</div>
              </div>
              <el-switch v-model="config.features.enableComment" />
            </div>
          </div>
        </div>
      </el-tab-pane>

      <!-- Tab 2: 积分规则 -->
      <el-tab-pane label="积分规则" name="scoring">
        <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-6 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
          <div class="flex items-center gap-2 mb-3">
            <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h4 class="text-sm font-black text-slate-900">积分权重配置</h4>
          </div>
          <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

          <div class="bg-blue-50 text-blue-700 rounded-lg p-3 flex items-start gap-2 text-sm mb-4">
            <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>修改权重后，专家积分会自动重新计算</span>
          </div>

          <el-form :model="config.scoring" label-width="90px" size="small">
            <el-form-item label="点击权重">
              <el-slider v-model="config.scoring.click" :min="0" :max="20" show-input />
            </el-form-item>
            <el-form-item label="播放权重">
              <el-slider v-model="config.scoring.play" :min="0" :max="20" show-input />
            </el-form-item>
            <el-form-item label="收藏权重">
              <el-slider v-model="config.scoring.bookmark" :min="0" :max="20" show-input />
            </el-form-item>
            <el-form-item label="点赞权重">
              <el-slider v-model="config.scoring.like" :min="0" :max="20" show-input />
            </el-form-item>
            <el-form-item label="评论权重">
              <el-slider v-model="config.scoring.comment" :min="0" :max="20" show-input />
            </el-form-item>
          </el-form>
        </div>
      </el-tab-pane>

      <!-- Tab 3: 匹配与风险 -->
      <el-tab-pane label="匹配与风险" name="matching">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-6 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
            <div class="flex items-center gap-2 mb-3">
              <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <h4 class="text-sm font-black text-slate-900">专家匹配算法权重</h4>
            </div>
            <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

            <div class="bg-blue-50 text-blue-700 rounded-lg p-3 flex items-start gap-2 text-sm mb-4">
              <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>三项权重之和应等于 1.0（100%）</span>
            </div>

            <el-form :model="config.matching" label-width="90px" size="small">
              <el-form-item label="话题匹配">
                <el-slider v-model="config.matching.topicMatch" :min="0" :max="1" :step="0.05" show-input />
              </el-form-item>
              <el-form-item label="风格匹配">
                <el-slider v-model="config.matching.styleMatch" :min="0" :max="1" :step="0.05" show-input />
              </el-form-item>
              <el-form-item label="阶段匹配">
                <el-slider v-model="config.matching.stageMatch" :min="0" :max="1" :step="0.05" show-input />
              </el-form-item>
              <el-form-item>
                <div
                  :class="[
                    'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-lg',
                    matchingSum === 1
                      ? 'bg-emerald-50 text-emerald-600'
                      : 'bg-red-50 text-red-600'
                  ]"
                >
                  <svg v-if="matchingSum === 1" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <svg v-else class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  总和: {{ matchingSum.toFixed(2) }}
                  <span v-if="matchingSum !== 1">（必须等于 1.0）</span>
                </div>
              </el-form-item>
            </el-form>
          </div>

          <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-6 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
            <div class="flex items-center gap-2 mb-3">
              <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h4 class="text-sm font-black text-slate-900">风险评分阈值</h4>
            </div>
            <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

            <el-form :model="config.risk" label-width="90px" size="small">
              <el-form-item label="极高危阈值">
                <el-slider v-model="config.risk.extreme" :min="50" :max="100" show-input />
              </el-form-item>
              <el-form-item label="高危阈值">
                <el-slider v-model="config.risk.high" :min="30" :max="90" show-input />
              </el-form-item>
              <el-form-item label="中危阈值">
                <el-slider v-model="config.risk.medium" :min="10" :max="70" show-input />
              </el-form-item>
            </el-form>
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- 底部操作确认卡片 -->
    <div v-if="hasPerm(PERMISSIONS.EDIT_CONFIG)" class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-6 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <div class="flex items-center gap-2 mb-3">
        <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h4 class="text-sm font-black text-slate-900">操作确认</h4>
      </div>
      <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

      <div class="bg-amber-50 text-amber-700 rounded-lg p-3 flex items-start gap-2 text-sm mb-4">
        <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span>修改配置将记录审计日志，请填写操作理由</span>
      </div>

      <el-form size="small">
        <el-form-item label="修改理由" required>
          <el-input
            v-model="auditReason"
            type="textarea"
            :rows="2"
            placeholder="请说明修改配置的理由，将记录到审计日志"
            class="config-textarea"
          />
        </el-form-item>
        <el-form-item>
          <div class="flex items-center gap-3">
            <button
              @click="handleReset"
              class="px-4 py-2 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50 transition-colors"
            >
              恢复默认值
            </button>
            <button
              @click="handleSave"
              class="px-5 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors shadow-sm"
            >
              保存变更
            </button>
          </div>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { SCORING_CONFIG, MATCHING_CONFIG, RISK_CONFIG, FEATURE_FLAGS, PERMISSIONS } from '../../data/adminConfig'
import { logAction } from '../../services/audit'
import { calculateExpertPoints } from '../../data/experts'
import { mapGetters } from 'vuex'

const DEFAULT_CONFIG = {
  scoring: { ...SCORING_CONFIG.weights },
  matching: { ...MATCHING_CONFIG.weights },
  risk: { ...RISK_CONFIG.thresholds },
  features: { ...FEATURE_FLAGS },
}

export default {
  name: 'ConfigManager',

  data() {
    return {
      activeTab: 'features',
      config: {
        scoring: { ...DEFAULT_CONFIG.scoring },
        matching: { ...DEFAULT_CONFIG.matching },
        risk: { ...DEFAULT_CONFIG.risk },
        features: { ...DEFAULT_CONFIG.features },
      },
      auditReason: '',
    }
  },

  computed: {
    ...mapGetters(['hasPermission']),

    PERMISSIONS() {
      return PERMISSIONS
    },

    matchingSum() {
      return this.config.matching.topicMatch + this.config.matching.styleMatch + this.config.matching.stageMatch
    },
  },

  methods: {
    hasPerm(permission) {
      return this.hasPermission(permission)
    },

    handleSave() {
      if (!this.hasPerm(PERMISSIONS.EDIT_CONFIG)) {
        this.$message.error('您没有编辑配置的权限')
        return
      }
      if (!this.auditReason.trim()) {
        this.$message.error('请填写修改理由')
        return
      }

      if (this.matchingSum !== 1) {
        this.$message.error('匹配权重之和必须等于 1.0')
        return
      }

      // 记录审计日志
      logAction({
        action: 'CONFIG_CHANGE',
        targetType: 'config',
        targetId: 'system-config',
        reason: this.auditReason,
        source: 'admin-ui',
        actor: { role: this.$store.state.userRole },
      })

      // 更新积分权重（同步到 store）
      const updatedExperts = this.$store.state.experts.map(expert => {
        const points = calculateExpertPoints(expert.stats, this.config.scoring)
        return { ...expert, points }
      }).sort((a, b) => b.points - a.points)

      this.$store.commit('SET_EXPERTS', updatedExperts)

      this.$message.success('配置已保存，专家积分已重新计算')
      this.auditReason = ''
    },

    handleReset() {
      this.$confirm('确定恢复所有配置为默认值？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        this.config = {
          scoring: { ...DEFAULT_CONFIG.scoring },
          matching: { ...DEFAULT_CONFIG.matching },
          risk: { ...DEFAULT_CONFIG.risk },
          features: { ...DEFAULT_CONFIG.features },
        }
        this.$message.success('已恢复默认值')
      }).catch(() => {})
    },
  },
}
</script>

<style scoped>
/* 调整 ElementUI 滑块和开关在 Tailwind 卡片中的样式 */
.config-manager >>> .el-form-item__label {
  color: #475569;
  font-weight: 500;
}

.config-manager >>> .el-slider__runway {
  margin-right: 70px;
}

.config-manager >>> .el-switch__label {
  color: #64748b;
}

/* textarea 样式统一 */
.config-manager >>> .config-textarea .el-textarea__inner {
  background-color: #f8fafc;
  border-color: #e2e8f0;
  border-radius: 0.75rem;
}

.config-manager >>> .config-textarea .el-textarea__inner:focus {
  border-color: #F2C94C;
  box-shadow: 0 0 0 3px rgba(242, 201, 76, 0.15);
}
</style>
